// The lesson recorder (Nov 2026): how well a class game runs on this laptop, so the teacher (and we) can see which
// laptops struggle and how often the class drops out, without asking anyone. Nothing here is about the student: no
// name, no player code, no answers. Just this laptop's own numbers:
//   - how long its frames took (a histogram, in ms), and how many were slow (> 50 ms) or stalls (> 250 ms), for the
//     whole lesson and for each game played (and 'lobby': between games), so a slow game stands out
//   - how often it joined the room, dropped out, came back (and how long that took), and reloaded the page
//   - between games, the same again for each place in the world its slime was in ('z-yard', 'z-village', 'z-cellar'…),
//     as the yard costs far more to draw than a class room (Spencer, Nov 4 2026)
//   - its graphics setting (and where Auto settled), and coarse hardware: cores, memory, screen size, system and browser
// useRoomSocket (client.ts) sends a report to the room every minute (and once soon after joining); the room adds them up into the lesson's log,
// which goes to the teacher who opened it (Results > How lessons ran).
//
// A laptop is a random id kept in this browser (gk-laptop), so a reload counts as the same laptop. The counts for a
// room are kept in this tab (sessionStorage) too, so a page that reloads keeps counting from where it was.

import { graphicsLabel } from '../slime/graphicsPref';

const LAPTOP_KEY = 'gk-laptop';
const HIST = 251; // 0..250 ms, the last one "250 or more"
const SCENES_MAX = 20; // games, the lobby and its places (z-yard, z-cellar…) counted apart in one lesson

export type MeterRole = 'p' | 'h' | 'c'; // a student laptop, the projector, a control laptop

export interface MeterReport {
  t: 'meter';
  id: string;
  role: MeterRole;
  n: number; // frames counted
  p50: number; // ms
  p95: number;
  p99: number;
  slow: number; // frames over 50 ms
  stalls: number; // frames over 250 ms (the screen froze)
  joins: number;
  drops: number;
  reloads: number;
  rejoin: number[]; // ms from each drop to being back in the room (the last ten)
  scenes: Record<string, SceneReport>; // the same frame numbers for each game played, and 'lobby' (between games)
  gfx: string;
  cores: number;
  mem: number;
  w: number;
  h: number;
  dpr: number;
  os: string;
  br: string;
}

export interface SceneReport {
  n: number;
  p50: number;
  p95: number;
  slow: number;
  stalls: number;
}

interface Frames {
  hist: number[];
  n: number;
  slow: number;
  stalls: number;
}

interface Saved extends Frames {
  scenes: Record<string, Frames>;
  joins: number;
  drops: number;
  reloads: number;
  rejoin: number[];
}

function laptopId(): string {
  try {
    let id = localStorage.getItem(LAPTOP_KEY);
    if (!id || !/^[a-z0-9]{8}$/.test(id)) {
      id = Array.from(crypto.getRandomValues(new Uint8Array(8)), (b) => 'abcdefghijklmnopqrstuvwxyz0123456789'[b % 36]).join('');
      localStorage.setItem(LAPTOP_KEY, id);
    }
    return id;
  } catch {
    return 'nostore0';
  }
}

function system(): { os: string; br: string } {
  const ua = navigator.userAgent;
  const os = /CrOS/.test(ua) ? 'ChromeOS' : /Windows/.test(ua) ? 'Windows' : /iPad|iPhone/.test(ua) ? 'iOS' : /Mac OS X/.test(ua) ? 'Mac' : /Android/.test(ua) ? 'Android' : /Linux/.test(ua) ? 'Linux' : 'other';
  const m = ua.match(/Edg\/(\d+)/) ?? ua.match(/Firefox\/(\d+)/) ?? ua.match(/Chrome\/(\d+)/) ?? ua.match(/Version\/(\d+).*Safari/);
  const name = /Edg\//.test(ua) ? 'Edge' : /Firefox\//.test(ua) ? 'Firefox' : /Chrome\//.test(ua) ? 'Chrome' : /Safari\//.test(ua) ? 'Safari' : 'other';
  return { os, br: m ? `${name} ${m[1]}` : name };
}


const pct = (hist: number[], n: number, p: number) => {
  if (!n) return 0;
  const want = n * p;
  let seen = 0;
  for (let i = 0; i < hist.length; i++) {
    seen += hist[i];
    if (seen >= want) return i;
  }
  return HIST - 1;
};

function count(f: Frames, d: number) {
  f.hist[Math.min(HIST - 1, Math.round(d))]++;
  f.n++;
  if (d > 50) f.slow++;
  if (d > 250) f.stalls++;
}

class Meter {
  private room: string | null = null;
  private role: MeterRole = 'p';
  private users = 0;
  private s: Saved = Meter.fresh();
  private raf = 0;
  private last = 0;
  private droppedAt = 0;
  private scene = ''; // what's on screen now: a game's mode, or 'lobby'
  private zone = ''; // between games, the place my slime is in (the lobby says: lobby.ts)

  private static frames(): Frames {
    return { hist: new Array(HIST).fill(0), n: 0, slow: 0, stalls: 0 };
  }
  private static fresh(): Saved {
    return { ...Meter.frames(), scenes: {}, joins: 0, drops: 0, reloads: 0, rejoin: [] };
  }

  // A socket to this room starts (a page can open the same room again: it's the same count)
  attach(room: string, role: MeterRole) {
    if (this.room !== room) {
      this.save();
      this.room = room;
      this.role = role;
      this.s = this.load(room);
      this.users = 0;
    }
    this.users++;
    if (!this.raf) this.tick(0);
  }
  detach(room: string) {
    if (this.room !== room) return;
    this.save();
    if (--this.users > 0) return;
    cancelAnimationFrame(this.raf);
    this.raf = 0;
    this.room = null;
  }

  // in the room (the room answered): a join, and the end of a drop if there was one
  joined() {
    this.s.joins++;
    if (this.droppedAt) {
      this.s.rejoin = [...this.s.rejoin, Math.min(600_000, Date.now() - this.droppedAt)].slice(-10);
      this.droppedAt = 0;
    }
  }
  // the room says what's on: a game (its mode) or the lobby between games
  setScene(key: string) {
    this.scene = key.replace(/[^a-z0-9-]/gi, '').toLowerCase().slice(0, 24);
  }
  // between games: the place in the world my slime is in now ('' while something covers the lobby)
  setZone(key: string) {
    this.zone = key.replace(/[^a-z0-9-]/gi, '').toLowerCase().slice(0, 20);
  }
  // the connection broke and this laptop is trying again
  dropped() {
    if (this.droppedAt) return; // still coming back from the last one
    this.s.drops++;
    this.droppedAt = Date.now();
  }

  report(): MeterReport | null {
    if (!this.room) return null;
    this.save();
    const { hist, n } = this.s;
    return {
      t: 'meter',
      id: laptopId(),
      role: this.role,
      n,
      p50: pct(hist, n, 0.5),
      p95: pct(hist, n, 0.95),
      p99: pct(hist, n, 0.99),
      slow: this.s.slow,
      stalls: this.s.stalls,
      joins: this.s.joins,
      drops: this.s.drops,
      reloads: this.s.reloads,
      rejoin: this.s.rejoin,
      scenes: Object.fromEntries(
        Object.entries(this.s.scenes).map(([k, f]) => [k, { n: f.n, p50: pct(f.hist, f.n, 0.5), p95: pct(f.hist, f.n, 0.95), slow: f.slow, stalls: f.stalls }]),
      ),
      gfx: graphicsLabel(),
      cores: navigator.hardwareConcurrency || 0,
      mem: (navigator as Navigator & { deviceMemory?: number }).deviceMemory || 0,
      w: screen.width,
      h: screen.height,
      dpr: Math.round((devicePixelRatio || 1) * 100) / 100,
      ...system(),
    };
  }

  // Frame times while the page is in sight. A gap over two seconds is the tab hidden or the laptop asleep, not a
  // frame, so it isn't counted.
  private tick = (t: number) => {
    this.raf = requestAnimationFrame(this.tick);
    if (this.last && document.visibilityState === 'visible') {
      const d = t - this.last;
      if (d > 0 && d < 2000) {
        count(this.s, d);
        if (this.scene) {
          const sc = this.s.scenes;
          if (!sc[this.scene] && Object.keys(sc).length < SCENES_MAX) sc[this.scene] = Meter.frames();
          if (sc[this.scene]) count(sc[this.scene], d);
          const z = this.scene === 'lobby' && this.zone ? `z-${this.zone}` : '';
          if (z && !sc[z] && Object.keys(sc).length < SCENES_MAX) sc[z] = Meter.frames();
          if (z && sc[z]) count(sc[z], d);
        }
      }
    }
    this.last = t;
  };

  private load(room: string): Saved {
    try {
      const raw = sessionStorage.getItem(`gk-meter:${room}`);
      if (raw) {
        const s = JSON.parse(raw) as Saved;
        if (Array.isArray(s.hist) && s.hist.length === HIST) return { ...s, scenes: s.scenes ?? {}, reloads: (s.reloads ?? 0) + 1 };
      }
    } catch {
      /* fresh */
    }
    return Meter.fresh();
  }
  private save() {
    if (!this.room) return;
    try {
      sessionStorage.setItem(`gk-meter:${this.room}`, JSON.stringify(this.s));
    } catch {
      /* not kept: a reload starts again */
    }
  }
}

export const meter = new Meter();

// /rooms/<code>/ws?role=…: a class room (the after-school yard and the cellar aren't lessons)
export function meterRoom(url: string): { room: string; role: MeterRole } | null {
  try {
    const u = new URL(url);
    const m = u.pathname.match(/\/rooms\/(\d{4,8})\/ws$/);
    if (!m) return null;
    const host = u.searchParams.get('role') === 'host';
    return { room: m[1], role: host ? (u.searchParams.get('view') === 'controls' ? 'c' : 'h') : 'p' };
  } catch {
    return null;
  }
}
